<template>
  <section
    class="invoice-preview-wrapper"
    style="position: relative"
  >
    <!-- Alert: No items found -->
    <b-alert
      variant="danger"
      :show="vouchers.length === 0 && !isLoading"
    >
      <h4 class="alert-heading">
        Erreur lors du chargement des bons d'échange
      </h4>
      <div class="alert-body">
        Aucun bon d'échange trouvé. Vérifiez les références.
      </div>
    </b-alert>

    <!-- Loading state -->
    <div
      v-if="isLoading"
      class="text-center py-5"
    >
      <div
        class="spinner-border text-primary"
        role="status"
      >
        <span class="sr-only">Chargement...</span>
      </div>
      <p class="mt-2 text-muted">
        Chargement des bons d'échange...
      </p>
    </div>

    <!-- Print All Button -->
    <b-row
      v-if="vouchers.length > 0"
      class="mb-3"
    >
      <b-col cols="12">
        <div class="d-flex justify-content-end">
          <b-button
            variant="primary"
            @click="printAll"
          >
            <feather-icon
              icon="PrinterIcon"
              class="mr-1"
            />
            Imprimer tous ({{ vouchers.length }})
          </b-button>
        </div>
      </b-col>
    </b-row>

    <!-- Vouchers Display -->
    <div
      v-for="(voucher, index) in vouchers"
      :key="voucher.id"
      class="voucher-section"
      :class="{ 'page-break': index < vouchers.length - 1 }"
    >
      <b-row class="invoice-preview">
        <b-col
          cols="12"
          xl="9"
          md="8"
        >
          <b-card
            no-body
            class="invoice-preview-card"
          >
            <div class="d-flex justify-content-between flex-wrap mr-1 ml-1">
              <div class="invoice-preview">
                <strong
                  class="mt-1"
                  style="color: #0A2634; font-size: 40px"
                >
                  Bon d'échange
                </strong>
                <h3 style="color: black">
                  Date:
                  <span
                    class="invoice-date"
                    style="color: black"
                  >
                    {{ formatFn(voucher.created_at) }}
                  </span>
                </h3>
                <h3 style="color: black">
                  Nombre de colis échangés: <strong>{{ voucher.orders_count }}</strong>
                </h3>
                <div class="d-flex justify-content-between mt-1">
                  <div>
                    <qrcode-vue
                      :value="voucher.id.toString()"
                      :size="size"
                      level="H"
                    />
                    <span>{{ voucher.id }}</span>
                  </div>
                  <div>
                    <barcode
                      height="30"
                      width="2"
                      :value="voucher.id.toString()"
                      format="CODE128"
                    />
                  </div>
                </div>
              </div>

              <div class="logo-wrapper">
                <img
                  src="@/assets/images/logo/logo.svg"
                  height="100"
                  width="100"
                >
                <h3 class="text-primary invoice-logo">
                  {{ company.name || $branding.company.name }}
                </h3>
              </div>
              <b-card-text class="mb-25">
                <strong>{{ company.name || $branding.company.name }} : {{ company.tagline || $branding.company.tagline }}</strong>
              </b-card-text>
              <b-card-text class="mb-25">
                <strong>Matricule fiscale : {{ company.tax_identification_number }}</strong>
              </b-card-text>
              <b-card-text class="mb-25">
                <strong>Adresse : {{ company.address }}</strong>
              </b-card-text>
              <b-card-text class="mb-0">
                <strong>Téléphone : {{ company.phone1 }}</strong>
              </b-card-text>
            </div>

            <hr class="invoice-spacing">

            <!-- Orders Table -->
            <b-card-body class="invoice-padding pt-0">
              <b-row>
                <b-col cols="12">
                  <h4 class="mb-2">
                    Colis Échange {{ voucher.orders_count }}
                  </h4>
                </b-col>
              </b-row>
            </b-card-body>

            <b-table-lite
              responsive
              :items="voucher.orders"
              :fields="fields"
              class="mb-0"
            >
              <template #cell(code)="data">
                <div class="text-center">
                  <barcode
                    height="30"
                    width="2"
                    :value="data.item.id.toString()"
                    format="CODE128"
                  />
                  <div class="mt-1">
                    <strong style="color: #0A2634">Échange</strong>
                  </div>
                </div>
              </template>

              <template #cell(expediteur)="data">
                {{ data.item.supplier_name || 'N/A' }}
              </template>

              <template #cell(gouvernorat)="data">
                {{ data.item.governorate_name || 'N/A' }}
              </template>

              <template #cell(tel)="data">
                {{ data.item.phone1 }}
              </template>

              <template #cell(destinataire)="data">
                {{ data.item.name }}
              </template>

              <template #cell(adresse)="data">
                {{ data.item.address }}
              </template>

              <template #cell(prix)="data">
                {{ data.item.price_ttc }}
              </template>
            </b-table-lite>

            <hr class="invoice-spacing">

            <!-- Signature Section -->
            <b-card-body class="d-flex justify-content-between mt-5">
              <div class="mb-5 ml-1">
                <h3 style="color: black; font-size: 25px">
                  Signature
                </h3>
              </div>
            </b-card-body>

            <hr class="invoice-spacing">
          </b-card>
        </b-col>
      </b-row>
    </div>
  </section>
</template>

<script>
import axios from 'axios'
import {
  BCard, BCardBody, BCardText, BRow, BCol, BButton, BAlert, BTableLite,
} from 'bootstrap-vue'
import QrcodeVue from 'qrcode.vue'
import VueBarcode from 'vue-barcode'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import storeAuth from '@/store/store'

export default {
  components: {
    BCard,
    BCardBody,
    BCardText,
    BRow,
    BCol,
    BButton,
    BAlert,
    BTableLite,
    QrcodeVue,
    barcode: VueBarcode,
  },
  data() {
    return {
      vouchers: [],
      isLoading: false,
      size: 100,
      company: {},
      fields: [
        { key: 'code', label: 'CODE' },
        { key: 'expediteur', label: 'EXPÉDITEUR' },
        { key: 'gouvernorat', label: 'GOUVERNORAT' },
        { key: 'tel', label: 'TEL' },
        { key: 'destinataire', label: 'DESTINATAIRE' },
        { key: 'adresse', label: 'ADRESSE' },
        { key: 'prix', label: 'PRIX (DT)' },
      ],
    }
  },
  mounted() {
    this.loadCompanyData()
    this.loadVouchers()
  },
  methods: {
    loadCompanyData() {
      this.company = storeAuth.state.company || {}
    },
    async loadVouchers() {
      try {
        this.isLoading = true

        // Get voucher IDs from URL query parameter
        const voucherIds = this.$route.query.ids

        if (!voucherIds) {
          this.showToast('danger', 'top-center', 'Aucun bon d\'échange spécifié')
          this.isLoading = false
          return
        }

        // Parse IDs (can be comma-separated string or array)
        const ids = Array.isArray(voucherIds) ? voucherIds : voucherIds.split(',')

        // Call bulk print API to get all voucher data
        const { data } = await axios.post('/api/exchange-vouchers/bulk-print/', {
          exchange_voucher_ids: ids,
          mark_as_printed: false, // Don't mark as printed yet, only when user actually prints
        })

        this.vouchers = data.print_data
        this.isLoading = false
      } catch (error) {
        this.isLoading = false
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des bons d\'échange')
      }
    },
    printAll() {
      // Mark vouchers as printed
      this.markAsPrinted()

      // Trigger browser print
      window.print()
    },
    async markAsPrinted() {
      try {
        const voucherIds = this.vouchers.map(v => v.id)
        await axios.post('/api/exchange-vouchers/bulk-print/', {
          exchange_voucher_ids: voucherIds,
          mark_as_printed: true,
        })
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors de la mise à jour des bons d\'échange')
      }
    },
    formatFn(date) {
      if (!date) {
        return null
      }
      const [year, month, day] = date.split('-')
      const day1 = day.substr(0, 2)
      const h = date.slice(11, 19)
      return `${year}-${month.padStart(2, '0')}-${day1.padStart(2, '0')} | ${h}`
    },
    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: 'Notification',
            icon: 'InfoIcon',
            text,
            variant,
          },
        },
        {
          position,
        },
      )
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@core/scss/base/pages/app-invoice.scss';

.voucher-section {
  margin-bottom: 2rem;
}

@media print {
  .page-break {
    page-break-after: always;
  }

  .d-print-none,
  button {
    display: none !important;
  }

  .voucher-section {
    page-break-inside: avoid;
  }
}
</style>
